type JsonRecord = Record<string, unknown>;

export type PulseResultData = {
  columns: unknown[];
  rows: JsonRecord[];
};

export type PulseChartSpecContentResult =
  | { status: 'missing-spec' }
  | { status: 'invalid-spec' }
  | { status: 'missing-result-data' }
  | { status: 'invalid-result-data' }
  | { status: 'ready'; content: string };

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const compareKeys = (firstKey: string, secondKey: string): number => {
  if (firstKey < secondKey) {
    return -1;
  }

  if (firstKey > secondKey) {
    return 1;
  }

  return 0;
};

const sortJsonValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortJsonValue);
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value)
      .sort(([firstKey], [secondKey]) => compareKeys(firstKey, secondKey))
      .map(([key, childValue]) => [key, sortJsonValue(childValue)]),
  );
};

const stableStringify = (value: unknown): string =>
  JSON.stringify(sortJsonValue(value));

function stripInlineData(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(stripInlineData);
  }

  if (!isRecord(value)) {
    return value;
  }

  const next: JsonRecord = {};
  for (const [key, childValue] of Object.entries(value)) {
    if (key === 'data') {
      next[key] = stripDataReferences(childValue);
      continue;
    }

    next[key] = stripInlineData(childValue);
  }

  return next;
}

function stripDataReferences(data: unknown): unknown {
  if (Array.isArray(data)) {
    return data.map(stripDataReferences);
  }

  if (!isRecord(data)) {
    return stripInlineData(data);
  }

  const dataWithoutInlineValues = { ...data };
  delete dataWithoutInlineValues.values;
  delete dataWithoutInlineValues.url;
  return stripInlineData(dataWithoutInlineValues);
}

const getVisualizationSpecRecord = (
  visualizationSpec: unknown,
): JsonRecord | null => {
  if (!visualizationSpec) {
    return null;
  }

  if (typeof visualizationSpec === 'string') {
    try {
      const parsed = JSON.parse(visualizationSpec);
      return isRecord(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return isRecord(visualizationSpec) ? visualizationSpec : null;
};

const stripPulseVisualizationResultDataFromRecord = (
  spec: JsonRecord,
): JsonRecord | null => {
  const specWithoutDatasets: JsonRecord = {};
  for (const [key, value] of Object.entries(spec)) {
    if (key !== 'datasets') {
      specWithoutDatasets[key] = value;
    }
  }

  const resultlessSpec = stripInlineData(specWithoutDatasets);
  return isRecord(resultlessSpec) ? resultlessSpec : null;
};

export const stripPulseVisualizationResultData = (
  visualizationSpec: unknown,
): unknown => {
  const spec = getVisualizationSpecRecord(visualizationSpec);
  if (!spec) {
    return visualizationSpec;
  }

  return stripPulseVisualizationResultDataFromRecord(spec) ?? visualizationSpec;
};

export const parsePulseResultData = (
  resultData?: string | null,
): PulseResultData | null => {
  if (!resultData) {
    return null;
  }

  try {
    const parsed = JSON.parse(resultData);
    if (
      !isRecord(parsed) ||
      !Array.isArray(parsed.columns) ||
      !Array.isArray(parsed.rows)
    ) {
      return null;
    }

    const columns: unknown[] = parsed.columns;
    const rows: JsonRecord[] = [];
    for (const row of parsed.rows) {
      if (isRecord(row)) {
        rows.push(row);
      } else if (
        Array.isArray(row) &&
        row.length === columns.length &&
        columns.every(column => typeof column === 'string') &&
        new Set(columns).size === columns.length
      ) {
        rows.push(
          Object.fromEntries(
            columns.map((column, index) => [String(column), row[index]]),
          ),
        );
      } else {
        return null;
      }
    }

    return {
      columns,
      rows,
    };
  } catch {
    return null;
  }
};

export const getPulseChartSpecContent = ({
  visualizationSpec,
  resultData,
  maxHeight,
  hideTitle = false,
}: {
  visualizationSpec: unknown;
  resultData?: string | null;
  maxHeight?: number;
  hideTitle?: boolean;
}): PulseChartSpecContentResult => {
  if (!visualizationSpec) {
    return { status: 'missing-spec' };
  }

  const spec = getVisualizationSpecRecord(visualizationSpec);
  if (!spec) {
    return { status: 'invalid-spec' };
  }

  if (!resultData) {
    return { status: 'missing-result-data' };
  }

  const parsedResultData = parsePulseResultData(resultData);
  if (!parsedResultData) {
    return { status: 'invalid-result-data' };
  }

  const chartSpec = stripPulseVisualizationResultDataFromRecord(spec);
  if (!chartSpec || Array.isArray(chartSpec.data)) {
    return { status: 'invalid-spec' };
  }

  chartSpec.background = 'transparent';

  chartSpec.data = {
    ...(isRecord(chartSpec.data) ? chartSpec.data : {}),
    values: parsedResultData.rows,
  };

  if (
    typeof maxHeight === 'number' &&
    typeof chartSpec.height === 'number' &&
    chartSpec.height > maxHeight
  ) {
    chartSpec.height = maxHeight;
  }

  if (hideTitle) {
    delete chartSpec.title;
  }

  return {
    status: 'ready',
    content: stableStringify(chartSpec),
  };
};
